import React, { useContext, useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { AppPage, LoadingMask } from ".";
import { ActionType } from "../@types";
import { getLoggedInBeneInfo } from "../api";
import routes from "../app/routes";
import { AppContext } from "../app/store";
import {
  getBeneficiary,
  hasLoginSession,
  useTranslate,
  updateBeneInfo,
  logout,
} from "../helpers";
import { GetBeneficiaryParams } from "../helpers/beneficiaryInfoHelpers";
import { ApiResponseError } from "../helpers/errors";
import { handleMbpLandingRoute } from "../helpers/routeHelpers";
import { useFlags } from "launchdarkly-react-client-sdk";

const _GlobalSessionHandler: React.FC<RouteComponentProps> = ({
  children,
  history,
  location,
}) => {
  const isLoginCallbackRoute = location.pathname === routes.slsCallback;
  const isMbpLandingRoute = location.pathname === routes.mbpLandingPage;
  const { state, dispatch } = useContext(AppContext);
  const flags = useFlags();
  const [loading, setLoading] = useState(
    // Start with loading state set to true for callback routes
    // Callback routes always use the `redirect_uri` to send the user somewhere else
    isLoginCallbackRoute || isMbpLandingRoute
  );
  const t = useTranslate();

  useEffect(() => {
    (async () => {
      const getBeneficiaryParams: GetBeneficiaryParams = {
        state,
        dispatch,
        history,
        location,
        flags,
      };

      console.debug(
        `🐝 Inside GlobalSessionHandler, logged in? ${hasLoginSession()}`
      );

      // @TODO - MBP Landing page should be handled similarly to login callback route,
      // and should share some logic
      if (isMbpLandingRoute) {
        console.debug(`🐪 MBP landing route`);
        dispatch({ type: ActionType.RESET_STATE });
        setLoading(true);
        return handleMbpLandingRoute({
          dispatch,
          search: location.search,
          history,
          state,
          flags,
        }).then(() => setLoading(false));
      }

      try {
        let beneInfoWithHeaders;
        // the sls- and mbp-callback routes should get beneInfoWithHeaders by calling
        // to the API using the `code` query parameter, so this logic path
        // does not apply to those routes
        if (!(isLoginCallbackRoute || isMbpLandingRoute)) {
          // Get latest beneInfo
          beneInfoWithHeaders = await getLoggedInBeneInfo();
          const beneInfo = beneInfoWithHeaders.beneficiary;
          // If successful, the user is already logged in. If there's bene info already
          // in app state (store), just update bene info and return
          if (!!state.beneficiary && !!beneInfo) {
            console.debug(
              `🌸 Fetched and updating a bene successfully, returning from GlobalSessionHandler`
            );
            return updateBeneInfo({
              dispatch,
              beneInfo,
            });
          }
        }
        setLoading(true);
        // There is no bene in app state
        // At this point, need to fully update the store for a LI bene experience,
        // for any route
        // @TODO - this logic currently only applies to sls-callback, should be
        // extended to handle mbp-callback, and the mbp-callback handler above
        // eventually removed
        await getBeneficiary({
          ...getBeneficiaryParams,
          beneInfoWithHeaders,
        });
        setLoading(false);
      } catch (e) {
        console.debug(`🦄 Could not fecth a bene in GlobalSessionHandler`);
        let apiResponseStatus;
        if (e instanceof ApiResponseError) {
          const { info } = e;
          apiResponseStatus = info.apiResponseStatus;
        }
        // If app state (store) still holds bene info, we need to acivtely log
        // out, locally, clear everything out, and return to non-LI landing
        if (state.beneficiary) {
          console.debug(`🦞 Actively logging out`);
          logout({
            dispatch,
            apiExpired: apiResponseStatus === 401,
            clientExpired: true,
          });
        }
        // This likely means there is no session, and the page should proceed to
        // load unauthenticated
        // @TODO - May need to add conditional toast/routing here, even if there
        // is no bene in app state
        console.debug(`🐠 Continuing unauthenticated`);
        setLoading(false);
      }
    })();
  }, [isLoginCallbackRoute, isMbpLandingRoute]);

  return loading ? (
    <AppPage>
      <LoadingMask
        loading={true}
        message={t("loading.retrieving_your_information")}
      />
    </AppPage>
  ) : (
    <>{children}</>
  );
};

export const GlobalSessionHandler = withRouter(_GlobalSessionHandler);
